import type { LiteUserOut, TeamOut } from '@repo/api-codegen';
import {
	apiQueryKey,
	useApiGetMyMemberships,
	useApiGetTeam,
	useApiGetTeamListUsers,
	useApiUpdateTeam,
} from '@repo/api-codegen';
import { useQueryClient } from '@tanstack/react-query';
import { curry, find, noop } from 'lodash-es';
import { useAuthUser } from '../../../api';
import { useIsTeamsPage } from '../../../api/hooks/team';

type IUseHomepageTeamResult =
	| {
			isTeamsPage: true;
			team: TeamOut;
			isTeamViewerUser: boolean;
			isTeamEditorUser: boolean;
			teamMembers: LiteUserOut[];
			updateTeam: (
				key: keyof TeamOut
			) => (value: TeamOut[keyof TeamOut]) => void;
	  }
	| {
			isTeamsPage: false;
			team: null;
			isTeamViewerUser: boolean;
			isTeamEditorUser: boolean;
			teamMembers: [];
			updateTeam: VoidFunction;
	  };

export function useHomepageTeam(): IUseHomepageTeamResult {
	const queryClient = useQueryClient();
	const { isTeamsPage, teamId } = useIsTeamsPage();
	const { isAdminUser } = useAuthUser();

	const {
		isLoading,
		isError,
		data: team,
	} = useApiGetTeam(
		{
			pathParams: {
				teamId: teamId ?? '',
			},
		},
		{
			enabled: isTeamsPage && !!teamId,
		}
	);

	const { mutateAsync: updateTeam } = useApiUpdateTeam({});

	const { data: myMemberships } = useApiGetMyMemberships(
		{
			pathParams: {
				teamId: teamId ?? '',
			},
		},
		{
			enabled: !!teamId,
		}
	);

	const { data: teamMembers } = useApiGetTeamListUsers(
		{
			pathParams: {
				teamId: teamId ?? '',
			},
		},
		{
			enabled: isTeamsPage && !!teamId,
			select: ({ results }) => results,
		}
	);

	if (!isTeamsPage || !team || isLoading || isError) {
		return {
			isTeamsPage: false,
			isTeamViewerUser: false,
			isTeamEditorUser: false,
			team: null,
			teamMembers: [],
			updateTeam: noop,
		};
	}

	const handleUpdateTeam = curry(
		(key: keyof TeamOut, value: TeamOut[keyof TeamOut]) => {
			updateTeam(
				{
					pathParams: {
						teamId: team?.id ?? '',
					},
					body: {
						[key]: value,
					},
				},
				{
					onSettled: () => {
						queryClient.invalidateQueries({
							queryKey: apiQueryKey('auth/teams/{team_id}', {
								team_id: team?.id ?? '',
							}),
						});
					},
				}
			);
		}
	);

	const hasTeamWritePermission =
		find(myMemberships, { team_id: team.id })?.write === true;
	const isTeamViewerUser = !hasTeamWritePermission && !isAdminUser;
	const isTeamEditorUser = hasTeamWritePermission || isAdminUser;

	return {
		isTeamsPage: true,
		isTeamViewerUser,
		isTeamEditorUser,
		team,
		teamMembers: teamMembers || [],
		updateTeam: handleUpdateTeam,
	};
}
